import { useCallback } from "react";
import { useAppDispatch, useAppSelector, useAppStore } from "@/redux/hooks";
import { type TAmountFormData, updateForm } from "@/redux/slices/formSlice";
import type { RootState } from "@/redux/store";
import { EFormIds } from "@/utils/enums";

// Single cast point for the amount form slice -- necessary because formData uses
// a [key: string]: any index signature (see PNX-1749) that widens all key accesses.
export const selectAmountFormData = (state: RootState): TAmountFormData =>
  state.form.formData[EFormIds.Amount] as TAmountFormData;

const isAmountDataValid = (data: TAmountFormData): boolean =>
  Boolean(
    !data.amountIsLoading &&
      !data.amountInputPending &&
      !data.validationError &&
      data.processingValue
  );

export const useAmountForm = () => {
  const dispatch = useAppDispatch();
  const store = useAppStore();

  const formData = useAppSelector(selectAmountFormData);

  const updateAmountFormData = useCallback(
    (newFormData: Partial<TAmountFormData>) => {
      dispatch(
        updateForm({
          formId: EFormIds.Amount,
          formData: newFormData,
        })
      );
    },
    [dispatch]
  );

  const amountFormIsValid = isAmountDataValid(formData);

  // Reads validity directly from the Redux store, bypassing the React render
  // cycle. Use inside event handlers to avoid stale-closure race conditions.
  const getAmountFormIsValid = useCallback(
    () => isAmountDataValid(selectAmountFormData(store.getState())),
    [store]
  );

  return {
    // Reads live from the store (not the render closure) to avoid stale-closure
    // races, e.g. submitting right after editing a prefilled amount.
    getAmountFormData: useCallback(
      () => selectAmountFormData(store.getState()),
      [store]
    ),
    amountFormData: formData,
    amountIsLoading: formData.amountIsLoading,
    amountFormIsValid,
    getAmountFormIsValid,
    updateAmountFormData,
  };
};
